import { Component, Input, OnChanges, signal } from '@angular/core';

import { BetSlipType } from '@bpos';
import { BetState } from '@bpos/v1/my-bets';
import { MyBetsConfig, SitecoreMyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';

import { EpcotConfigService } from '../../../common/epcot-config.service';
import { EarlyPayoutOptionErrors, EarlyPayoutPickState, PayoutError, PayoutErrorPick } from '../../../my-bets-base/models/early-payout-types';
import { MyBetsBet, MyBetsBetSlip, MyBetsListContext } from '../../../my-bets-base/models/my-bets-viewmodels';
import { MyBetsUtilService } from '../../services/my-bets-util.service';

interface BetResult {
    bet: MyBetsBet;
    state: BetState | EarlyPayoutPickState;
}

@Component({
    selector: 'ms-my-bets-multi-bet-card',
    templateUrl: 'my-bets-multi-bet-card.html',
    styleUrl: 'my-bets-multi-bet-card.scss',
})
export class MyBetsMultiBetCardComponent implements OnChanges {
    @Input({ required: true }) betslip: MyBetsBetSlip;
    @Input({ required: true }) myBetsListContext: MyBetsListContext;
    @Input() earlyPayoutError?: PayoutError;
    @Input() enableFlash?: boolean;

    betResults: BetResult[] = [];
    cardTitle: string;
    betbuilderPicks: MyBetsBet[] = [];
    betbuilderLegs = '';
    isSystemBet = signal(false);
    isEpcotEnabled = false;

    get hasBetProtection(): boolean {
        return this.betslip.betProtectorInformation && this.betslip.betProtectorInformation.numberOfInsuredBets > 0;
    }

    get isLive(): boolean {
        return this.betslip.bets.some((b) => b.isLive);
    }

    get isCashoutMessageVisible(): boolean {
        return this.betslip.isRecoveringAfterEditBet;
    }

    get isIndicatorConnectorEnabledForMultiBets(): boolean {
        if (this.myBetsUtilService.isBetCardRedesigned()) {
            return this.betslip.betType !== BetSlipType.Single && this.myBetsConfig.isIndicatorConnectorEnabled;
        } else {
            return this.betslip.betType === BetSlipType.Combo && this.myBetsConfig.isIndicatorConnectorEnabled;
        }
    }

    constructor(
        public sitecoreMyBets: SitecoreMyBetsConfig,
        public myBetsConfig: MyBetsConfig,
        private stringInterpolationPipe: StringInterpolationPipe,
        private epcotConfigService: EpcotConfigService,
        public myBetsUtilService: MyBetsUtilService,
    ) {
        this.isEpcotEnabled = this.epcotConfigService.isEnabled();
    }

    ngOnChanges(): void {
        this.betResults = this.calculateResults(this.betslip.bets, this.earlyPayoutError);
        this.cardTitle = this.betslip.isTeaserBet ? this.sitecoreMyBets.myBets.TeaserBetTitle : this.betslip.typeTranslated;
        this.betbuilderPicks = this.betslip.bets.filter((bet) => bet.betBuilderInfo);
        this.isSystemBet.set(this.betslip.betType === BetSlipType.System);
        if (this.betbuilderPicks.length) {
            const legsCount = this.betslip.bets.length - this.betbuilderPicks.length + this.countBetbuilderLegs(this.betbuilderPicks);
            this.betbuilderLegs = this.stringInterpolationPipe.transform(this.sitecoreMyBets.myBets.BetbuilderComboHeaderLegs, { legsCount });
        }
    }

    getExpandedCardState(bet: MyBetsBet): BetState | EarlyPayoutPickState | undefined {
        return this.betResults.find((betState) => betState.bet === bet)?.state;
    }

    private calculateResults(bets: MyBetsBet[], error?: PayoutError): BetResult[] {
        const epErrors = this.getDisabledPicks(error);

        return bets.map((bet) => {
            const pickError = epErrors.find((e) => this.checkId(bet, e));

            if (pickError) {
                return { bet, state: pickError.state };
            }

            return { bet, state: bet.state };
        });
    }

    private getDisabledPicks(error?: PayoutError): PayoutErrorPick[] {
        if (!error || !this.isDisabledComboBetError(error)) {
            return [];
        }

        return error.picks?.filter((pick) => pick.state === EarlyPayoutPickState.Hidden || pick.state === EarlyPayoutPickState.Locked) ?? [];
    }

    private checkId(bet: MyBetsBet, error: PayoutErrorPick): boolean {
        return (
            error.id === bet.optionMarketInfo?.optionId ||
            error.id === bet.resultInfo?.optionId ||
            bet.participantInfo?.participantInfo[error.id] === 1
        );
    }

    private isDisabledComboBetError = (error: PayoutError) =>
        error.type === EarlyPayoutOptionErrors.OptionHiddenCombo || error.type === EarlyPayoutOptionErrors.OptionLockCombo;

    private countBetbuilderLegs(betbuilderPicks: MyBetsBet[]): number {
        return betbuilderPicks.reduce((acc, bet) => acc + bet.betBuilderInfo!.legs.length, 0);
    }
}
